import { useState, useEffect, useRef } from "react";

const C = {
  black: "#0D0D0D", white: "#FAFAF8", orange: "#E8622A", orangeLight: "#F07A45",
  orangeFaint: "#FDF0E8", cream: "#F5F1EB", ink: "#1A1A1A", inkMid: "#3D3D3D",
  inkLight: "#777", inkFaint: "#AAAAAA", border: "#E8E4DE", surface: "#F2EEE8",
  ai: "#3B5BDB", aiFaint: "#EEF2FF",
};

const FONT = { display: "'Bebas Neue', sans-serif", body: "'DM Sans', sans-serif" };

const WORDS = ["empowering.", "discovery.", "inspiring.", "flourishing.", "yours.", "inclusive."];

const ME = {
  name: "Alex Thompson", role: "Head of Humanities", school: "Greenwood Academy",
  country: "UK 🇬🇧", subjects: ["History", "Geography"], phase: "Secondary",
  yearsExp: 5, avatar: "AT", connections: 67, posts: 14, cpd: 2,
  bio: "Passionate about decolonising the curriculum and project-based learning. Five years in and still growing every day.",
};

const TEACHERS = [
  { id: 1, name: "Priya Sharma", role: "Science Lead", school: "Westfield Academy", country: "India 🇮🇳", subjects: ["Biology", "Chemistry"], yearsExp: 8, avatar: "PS", bio: "Inquiry-based learning advocate running cross-school science fairs. Looking for educators who believe in student-led discovery.", match: 94 },
  { id: 2, name: "David Okafor", role: "Head of Humanities", school: "Nairobi Academy", country: "Kenya 🇰🇪", subjects: ["History", "Humanities"], yearsExp: 11, avatar: "DO", bio: "African history specialist passionate about decolonising the curriculum through debate-based learning.", match: 96 },
  { id: 3, name: "Mei Lin", role: "Geography Lead", school: "Chengdu International", country: "China 🇨🇳", subjects: ["Geography", "Env. Studies"], yearsExp: 9, avatar: "ML", bio: "Human geography specialist running student-led climate projects globally.", match: 91 },
  { id: 4, name: "Aisha Mensah", role: "Head of English", school: "Lagos International", country: "Nigeria 🇳🇬", subjects: ["English", "Creative Writing"], yearsExp: 15, avatar: "AM", bio: "Published author building global pen-pal networks. Believes in cross-cultural education.", match: 82 },
  { id: 5, name: "James O'Brien", role: "Assistant Headteacher", school: "St Mary's Primary", country: "Ireland 🇮🇪", subjects: ["Literacy", "Leadership"], yearsExp: 12, avatar: "JO", bio: "School improvement specialist. Creator of MathsMonsters used in 300+ schools.", match: 88 },
];

const POSTS = [
  { id: 1, author: "David Okafor", avatar: "DO", role: "Head of Humanities · Nairobi Academy", time: "2h", content: "Unpopular opinion: the most powerful CPD you'll ever do is a 30-minute conversation with a teacher from a completely different context.\n\nGeography, phase, culture — all of it resets your assumptions in ways no course can.\n\nWho wants to have that conversation?", likes: 134, comments: 43, tags: ["CPD", "Leadership"], liked: true },
  { id: 2, author: "Priya Sharma", avatar: "PS", role: "Science Lead · Westfield Academy", time: "5h", content: "Just wrapped a cross-school virtual science fair with students from 5 countries. The depth of collaboration from students who'd never met was extraordinary.\n\nIf you're running cross-border projects — let's connect.", likes: 48, comments: 12, tags: ["STEM", "GlobalEd"], liked: false },
  { id: 3, author: "Aisha Mensah", avatar: "AM", role: "Head of English · Lagos International", time: "1d", content: "My Year 10s received their first pen-pal letters from Dublin and Mumbai today. One student read hers three times before looking up.\n\nThat moment is why I teach.", likes: 211, comments: 58, tags: ["EnglishTeaching", "GlobalConnections"], liked: false },
];

const COMMUNITIES = [
  { id: 1, name: "Education Leadership", members: 18400, joined: true },
  { id: 2, name: "Humanities Network", members: 7300, joined: true },
  { id: 3, name: "STEM Educators", members: 12400, joined: false },
  { id: 4, name: "Inclusive Education", members: 9100, joined: false },
  { id: 5, name: "EdTech & Innovation", members: 15600, joined: false },
  { id: 6, name: "Early Career Teachers", members: 6800, joined: false },
];

const AV_COLORS = { PS: "#C8401A", JO: "#1A5AC8", AM: "#8B3A8B", ML: "#1A8B6A", DO: "#E8622A", AT: C.black };

const Av = ({ i, size = 40 }) => (
  <div style={{ width: size, height: size, borderRadius: 3, background: AV_COLORS[i] || C.black, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontWeight: 400, fontSize: size * 0.38, fontFamily: FONT.display, flexShrink: 0, letterSpacing: "0.05em" }}>{i}</div>
);

const Tag = ({ children, accent }) => (
  <span style={{ background: accent ? C.orangeFaint : C.surface, color: accent ? C.orange : C.inkLight, border: `1px solid ${accent ? C.orange + "40" : C.border}`, borderRadius: 2, padding: "3px 8px", fontSize: 10, fontWeight: 700, fontFamily: FONT.body, letterSpacing: "0.07em", textTransform: "uppercase" }}>{children}</span>
);

const Toast = ({ msg }) => msg ? (
  <div style={{ position: "fixed", top: 20, left: "50%", transform: "translateX(-50%)", background: C.ink, color: C.white, borderRadius: 3, padding: "10px 22px", fontSize: 13, fontWeight: 600, fontFamily: FONT.body, zIndex: 9999, whiteSpace: "nowrap", boxShadow: "0 8px 32px rgba(0,0,0,0.2)" }}>{msg}</div>
) : null;

const NavBtn = ({ label, active, onClick }) => {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: "none", border: "none",
        color: active ? C.orange : hovered ? C.orange : C.white,
        fontSize: 10, fontWeight: 700, fontFamily: FONT.body,
        letterSpacing: "0.09em", cursor: "pointer",
        padding: "0 14px", height: 52,
        borderBottom: `2px solid ${active ? C.orange : "transparent"}`,
        transition: "all 0.15s",
      }}
    >{label}</button>
  );
};

// ── CYCLING WORD ──────────────────────────────────────────────────────────────
const CyclingWord = () => {
  const [idx, setIdx] = useState(0);
  const [show, setShow] = useState(true);
  useEffect(() => {
    const t = setInterval(() => {
      setShow(false);
      setTimeout(() => { setIdx(i => (i + 1) % WORDS.length); setShow(true); }, 250);
    }, 2000);
    return () => clearInterval(t);
  }, []);
  return (
    <span style={{ color: C.orange, display: "block", opacity: show ? 1 : 0, transform: show ? "translateY(0)" : "translateY(14px)", transition: "all 0.25s cubic-bezier(0.4,0,0.2,1)" }}>
      {WORDS[idx]}
    </span>
  );
};

// ── DISCOVER ──────────────────────────────────────────────────────────────────
const DiscoverView = ({ connected, setConnected, conversations, setConversations, showToast }) => {
  const [idx, setIdx] = useState(0);
  const [aiText, setAiText] = useState({});
  const [aiLoading, setAiLoading] = useState(false);

  useEffect(() => {
    const t = TEACHERS[idx];
    if (!t || aiText[t.id] !== undefined) return;
    setAiLoading(true);
    (async () => {
      try {
        const res = await fetch("https://api.anthropic.com/v1/messages", {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            model: "claude-sonnet-4-20250514", max_tokens: 500,
            system: "You match educators on Edify, a professional network for teachers and leaders. Write exactly 2 specific, energetic sentences on why two educators should connect. Focus on concrete collaboration. Return ONLY the 2 sentences.",
            messages: [{ role: "user", content: `A: ${ME.name}, ${ME.role}, teaches ${ME.subjects.join(" & ")}. Bio: ${ME.bio}\nB: ${t.name}, ${t.role}, teaches ${t.subjects.join(" & ")}. Bio: ${t.bio}\nWhy connect?` }]
          })
        });
        const data = await res.json();
        setAiText(p => ({ ...p, [t.id]: data.content?.find(b => b.type === "text")?.text || "Strong professional alignment — this connection could genuinely shift your practice." }));
      } catch { setAiText(p => ({ ...p, [t.id]: "Strong professional alignment — this connection could genuinely shift your practice." })); }
      setAiLoading(false);
    })();
  }, [idx]);

  const teacher = TEACHERS[idx];

  const doConnect = () => {
    setConnected(p => [...p, teacher.id]);
    if (!conversations.find(c => c.teacher.id === teacher.id))
      setConversations(cs => [...cs, { id: Date.now(), teacher, messages: [], unread: 0 }]);
    showToast(`Connected with ${teacher.name}`);
    setIdx(i => i + 1);
  };

  return (
    <div>
      <div style={{ marginBottom: 28 }}>
        <div style={{ display: "flex", alignItems: "baseline", gap: 12, marginBottom: 4 }}>
          <h2 style={{ fontFamily: FONT.display, fontSize: 48, color: C.ink, letterSpacing: "0.04em", lineHeight: 1 }}>DISCOVER</h2>
          <div style={{ background: C.ai, color: "#fff", fontSize: 9, fontWeight: 700, padding: "3px 8px", borderRadius: 2, letterSpacing: "0.08em", fontFamily: FONT.body }}>AI POWERED</div>
        </div>
        <p style={{ color: C.inkLight, fontSize: 13, fontFamily: FONT.body }}>Educators matched to your profile, phase and practice</p>
      </div>

      {idx < TEACHERS.length ? (
        <div>
          <div style={{ background: C.white, border: `1px solid ${C.border}`, position: "relative", overflow: "hidden" }}>
            <div style={{ height: 4, background: C.orange, width: `${teacher.match}%`, transition: "width 0.6s ease" }} />
            <div style={{ padding: "24px 24px 20px" }}>
              <div style={{ position: "absolute", top: 18, right: 20, fontFamily: FONT.display, fontSize: 36, color: C.orange, letterSpacing: "0.04em", lineHeight: 1 }}>{teacher.match}%</div>
              <div style={{ display: "flex", gap: 16, alignItems: "flex-start", marginBottom: 20, paddingRight: 64 }}>
                <Av i={teacher.avatar} size={62} />
                <div>
                  <div style={{ fontFamily: FONT.display, fontSize: 32, color: C.ink, letterSpacing: "0.04em", lineHeight: 1, marginBottom: 5 }}>{teacher.name.toUpperCase()}</div>
                  <div style={{ color: C.orange, fontWeight: 700, fontSize: 13, fontFamily: FONT.body }}>{teacher.role}</div>
                  <div style={{ color: C.inkLight, fontSize: 12, fontFamily: FONT.body, marginTop: 1 }}>{teacher.school} · {teacher.country}</div>
                </div>
              </div>
              <div style={{ borderLeft: `3px solid ${C.orange}`, paddingLeft: 14, marginBottom: 18 }}>
                <p style={{ color: C.inkMid, fontSize: 14, lineHeight: 1.7, fontFamily: FONT.body, margin: 0 }}>{teacher.bio}</p>
              </div>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 18 }}>
                {teacher.subjects.map(s => <Tag key={s}>{s}</Tag>)}
                <Tag accent>{teacher.yearsExp} yrs exp</Tag>
              </div>
              <div style={{ background: C.aiFaint, border: `1px solid ${C.ai}20`, padding: "12px 14px", marginBottom: 20 }}>
                <div style={{ fontSize: 9, fontWeight: 700, color: C.ai, letterSpacing: "0.1em", fontFamily: FONT.body, marginBottom: 6 }}>— AI MATCH INSIGHT</div>
                {aiLoading && !aiText[teacher.id] ? (
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <div style={{ width: 12, height: 12, border: `2px solid ${C.ai}30`, borderTop: `2px solid ${C.ai}`, borderRadius: "50%", animation: "spin 0.7s linear infinite", flexShrink: 0 }} />
                    <span style={{ fontSize: 13, color: C.ai, fontFamily: FONT.body }}>Analysing match…</span>
                  </div>
                ) : (
                  <p style={{ fontSize: 13, color: C.inkMid, fontFamily: FONT.body, lineHeight: 1.65, margin: 0 }}>{aiText[teacher.id]}</p>
                )}
              </div>
              <div style={{ display: "flex", gap: 10 }}>
                <button onClick={() => setIdx(i => i + 1)} style={{ flex: 1, padding: "13px 0", background: "transparent", border: `1px solid ${C.border}`, color: C.inkLight, fontWeight: 600, fontSize: 12, fontFamily: FONT.body, cursor: "pointer", letterSpacing: "0.06em" }}>PASS</button>
                <button onClick={doConnect} style={{ flex: 3, padding: "13px 0", background: C.orange, border: "none", color: "#fff", fontWeight: 700, fontSize: 13, fontFamily: FONT.body, cursor: "pointer", letterSpacing: "0.06em" }}>CONNECT WITH {teacher.name.split(" ")[0].toUpperCase()} →</button>
              </div>
            </div>
          </div>
          <div style={{ display: "flex", gap: 4, marginTop: 14 }}>
            {TEACHERS.map((_, i) => <div key={i} style={{ height: 3, flex: i === idx ? 2 : 1, background: i === idx ? C.orange : C.border, transition: "all 0.3s" }} />)}
          </div>
          <p style={{ color: C.inkFaint, fontSize: 11, fontFamily: FONT.body, marginTop: 8, letterSpacing: "0.05em" }}>{TEACHERS.length - idx} EDUCATORS LEFT</p>
          {connected.length > 0 && (
            <div style={{ marginTop: 24, borderTop: `1px solid ${C.border}`, paddingTop: 20 }}>
              <div style={{ fontFamily: FONT.display, fontSize: 20, color: C.ink, letterSpacing: "0.04em", marginBottom: 14 }}>NEW CONNECTIONS</div>
              <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
                {TEACHERS.filter(t => connected.includes(t.id)).map(t => (
                  <div key={t.id} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <Av i={t.avatar} size={36} />
                    <div>
                      <div style={{ fontFamily: FONT.display, fontSize: 16, color: C.ink, letterSpacing: "0.04em" }}>{t.name.split(" ")[0].toUpperCase()}</div>
                      <div style={{ fontSize: 11, color: C.orange, fontWeight: 600, fontFamily: FONT.body }}>{t.role}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      ) : (
        <div style={{ textAlign: "center", padding: "60px 20px", border: `1px solid ${C.border}` }}>
          <div style={{ fontFamily: FONT.display, fontSize: 56, color: C.orange, letterSpacing: "0.04em", marginBottom: 12 }}>DONE.</div>
          <p style={{ color: C.inkLight, fontFamily: FONT.body, fontSize: 14 }}>You connected with <strong style={{ color: C.orange }}>{connected.length}</strong> educators. Head to messages.</p>
        </div>
      )}
    </div>
  );
};

// ── FEED ──────────────────────────────────────────────────────────────────────
const FeedView = ({ posts, setPosts, showToast }) => {
  const [newPost, setNewPost] = useState("");
  return (
    <div>
      <h2 style={{ fontFamily: FONT.display, fontSize: 48, color: C.ink, letterSpacing: "0.04em", marginBottom: 4, lineHeight: 1 }}>FEED</h2>
      <p style={{ color: C.inkLight, fontSize: 13, fontFamily: FONT.body, marginBottom: 24 }}>What's moving the education world right now</p>
      <div style={{ border: `1px solid ${C.border}`, marginBottom: 20, background: C.white }}>
        <div style={{ display: "flex", gap: 14, padding: "16px 16px 0" }}>
          <Av i={ME.avatar} size={40} />
          <textarea value={newPost} onChange={e => setNewPost(e.target.value)} placeholder="Share your practice, a resource, a question, a win…" style={{ flex: 1, border: "none", outline: "none", fontSize: 14, fontFamily: FONT.body, resize: "none", minHeight: 72, color: C.ink, background: "transparent", lineHeight: 1.65 }} />
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderTop: `1px solid ${C.border}`, padding: "10px 14px", marginTop: 10 }}>
          <div style={{ display: "flex", gap: 16 }}>
            {["Resource", "Event", "Question"].map(l => <button key={l} style={{ background: "none", border: "none", color: C.inkFaint, fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: FONT.body, letterSpacing: "0.05em" }}>+ {l.toUpperCase()}</button>)}
          </div>
          <button onClick={() => { if (!newPost.trim()) return; setPosts(ps => [{ id: Date.now(), author: ME.name, avatar: ME.avatar, role: `${ME.role} · ${ME.school}`, time: "now", content: newPost, likes: 0, comments: 0, tags: [], liked: false }, ...ps]); setNewPost(""); showToast("Posted."); }} style={{ background: C.orange, color: "#fff", border: "none", padding: "9px 20px", fontWeight: 700, fontSize: 11, fontFamily: FONT.body, cursor: "pointer", letterSpacing: "0.08em" }}>POST →</button>
        </div>
      </div>
      {posts.map(post => (
        <div key={post.id} style={{ border: `1px solid ${C.border}`, background: C.white, marginBottom: 12 }}>
          <div style={{ padding: "18px 18px 0" }}>
            <div style={{ display: "flex", gap: 12, marginBottom: 14 }}>
              <Av i={post.avatar} size={44} />
              <div>
                <div style={{ fontFamily: FONT.display, fontSize: 20, color: C.ink, letterSpacing: "0.04em", lineHeight: 1 }}>{post.author.toUpperCase()}</div>
                <div style={{ fontSize: 11, color: C.inkFaint, fontFamily: FONT.body, marginTop: 3 }}>{post.role} · {post.time}</div>
              </div>
            </div>
            <p style={{ color: C.inkMid, fontSize: 14, lineHeight: 1.75, fontFamily: FONT.body, marginBottom: 14, whiteSpace: "pre-line" }}>{post.content}</p>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 14 }}>
              {post.tags.map(t => <span key={t} style={{ fontSize: 11, color: C.orange, fontFamily: FONT.body, fontWeight: 700 }}>#{t}</span>)}
            </div>
          </div>
          <div style={{ display: "flex", borderTop: `1px solid ${C.border}` }}>
            {[
              { label: `${post.likes} LIKES`, active: post.liked, action: () => setPosts(ps => ps.map(p => p.id === post.id ? { ...p, liked: !p.liked, likes: p.liked ? p.likes - 1 : p.likes + 1 } : p)) },
              { label: `${post.comments} COMMENTS`, active: false, action: null },
              { label: "SHARE", active: false, action: null },
            ].map(({ label, active, action }, i) => (
              <button key={label} onClick={action} style={{ flex: 1, background: active ? C.orangeFaint : "none", border: "none", borderRight: i < 2 ? `1px solid ${C.border}` : "none", padding: "10px 0", cursor: action ? "pointer" : "default", color: active ? C.orange : C.inkFaint, fontWeight: 700, fontSize: 10, fontFamily: FONT.body, letterSpacing: "0.07em" }}>{label}</button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

// ── MESSAGES ──────────────────────────────────────────────────────────────────
const MessagesView = ({ conversations, setConversations }) => {
  const [activeId, setActiveId] = useState(null);
  const [input, setInput] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [loadingSugg, setLoadingSugg] = useState(false);
  const bottomRef = useRef(null);
  const active = conversations.find(c => c.id === activeId);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [active?.messages?.length]);

  const open = (id) => { setActiveId(id); setSuggestions([]); setConversations(cs => cs.map(c => c.id === id ? { ...c, unread: 0 } : c)); };

  const send = (text) => {
    if (!text.trim() || !activeId) return;
    const msg = { from: "me", text, time: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) };
    setConversations(cs => cs.map(c => c.id === activeId ? { ...c, messages: [...c.messages, msg] } : c));
    setInput(""); setSuggestions([]);
  };

  const getAI = async () => {
    if (!active) return;
    setLoadingSugg(true); setSuggestions([]);
    try {
      const history = active.messages.slice(-5).map(m => `${m.from === "me" ? ME.name : active.teacher.name}: ${m.text}`).join("\n");
      const res = await fetch("https://api.anthropic.com/v1/messages", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          model: "claude-sonnet-4-20250514", max_tokens: 500,
          system: `You assist ${ME.name} (${ME.role}) on Edify, a professional education network. Generate 3 short, warm, collegial reply suggestions. 1-2 sentences each. Return ONLY a JSON array of 3 strings.`,
          messages: [{ role: "user", content: `Conversation:\n${history}\n\nGenerate 3 replies.` }]
        })
      });
      const data = await res.json();
      const raw = data.content?.find(b => b.type === "text")?.text || "[]";
      setSuggestions(JSON.parse(raw.replace(/```json|```/g, "").trim()));
    } catch { setSuggestions(["That really resonates — tell me more.", "Would love to collaborate on that.", "Let's find time to connect properly on this."]); }
    setLoadingSugg(false);
  };

  return (
    <div>
      <div style={{ display: "flex", alignItems: "baseline", gap: 12, marginBottom: 20 }}>
        <h2 style={{ fontFamily: FONT.display, fontSize: 48, color: C.ink, letterSpacing: "0.04em", lineHeight: 1 }}>MESSAGES</h2>
        {conversations.reduce((n,c)=>n+c.unread,0) > 0 && <div style={{ background: C.orange, color: "#fff", fontSize: 10, fontWeight: 700, padding: "2px 9px", borderRadius: 2, fontFamily: FONT.body, letterSpacing: "0.06em" }}>{conversations.reduce((n,c)=>n+c.unread,0)} NEW</div>}
      </div>
      <div style={{ display: "flex", border: `1px solid ${C.border}`, height: "calc(100vh - 200px)", background: C.white }}>
        <div style={{ width: active ? "38%" : "100%", borderRight: active ? `1px solid ${C.border}` : "none", overflowY: "auto" }}>
          {conversations.map(conv => {
            const last = conv.messages[conv.messages.length - 1];
            return (
              <div key={conv.id} onClick={() => open(conv.id)} style={{ padding: "14px 16px", borderBottom: `1px solid ${C.border}`, cursor: "pointer", background: activeId === conv.id ? C.orangeFaint : "transparent", borderLeft: `3px solid ${activeId === conv.id ? C.orange : "transparent"}`, transition: "all 0.15s" }}>
                <div style={{ display: "flex", gap: 10 }}>
                  <div style={{ position: "relative", flexShrink: 0 }}>
                    <Av i={conv.teacher.avatar} size={38} />
                    {conv.unread > 0 && <div style={{ position: "absolute", top: -3, right: -3, width: 10, height: 10, background: C.orange, borderRadius: "50%", border: `2px solid ${C.white}` }} />}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                      <span style={{ fontFamily: FONT.display, fontSize: 15, color: C.ink, letterSpacing: "0.04em" }}>{conv.teacher.name.toUpperCase()}</span>
                      <span style={{ fontSize: 10, color: C.inkFaint, fontFamily: FONT.body }}>{last?.time}</span>
                    </div>
                    <div style={{ fontSize: 12, color: C.inkLight, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", fontFamily: FONT.body, marginTop: 2 }}>{last?.text || "Start the conversation"}</div>
                    <div style={{ fontSize: 10, color: C.orange, fontWeight: 700, fontFamily: FONT.body, marginTop: 2, letterSpacing: "0.04em" }}>{conv.teacher.role}</div>
                  </div>
                </div>
              </div>
            );
          })}
          {conversations.length === 0 && <div style={{ padding: 32, textAlign: "center", color: C.inkFaint, fontFamily: FONT.body, fontSize: 13 }}>Connect with educators in Discover to start conversations.</div>}
        </div>
        {active ? (
          <div style={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0 }}>
            <div style={{ padding: "12px 16px", borderBottom: `1px solid ${C.border}`, display: "flex", alignItems: "center", gap: 12, background: C.cream }}>
              <button onClick={() => setActiveId(null)} style={{ background: "none", border: "none", cursor: "pointer", color: C.inkLight, fontSize: 18, padding: 0 }}>←</button>
              <Av i={active.teacher.avatar} size={34} />
              <div>
                <div style={{ fontFamily: FONT.display, fontSize: 17, color: C.ink, letterSpacing: "0.04em" }}>{active.teacher.name.toUpperCase()}</div>
                <div style={{ fontSize: 11, color: C.inkFaint, fontFamily: FONT.body }}>{active.teacher.role}</div>
              </div>
            </div>
            <div style={{ flex: 1, overflowY: "auto", padding: 16, display: "flex", flexDirection: "column", gap: 10, background: C.surface }}>
              {active.messages.length === 0 && <div style={{ textAlign: "center", padding: "40px 20px", color: C.inkFaint, fontFamily: FONT.body, fontSize: 13 }}>You're connected. Start the conversation.</div>}
              {active.messages.map((msg, i) => (
                <div key={i} style={{ display: "flex", justifyContent: msg.from === "me" ? "flex-end" : "flex-start" }}>
                  <div style={{ maxWidth: "75%", background: msg.from === "me" ? C.orange : C.white, color: msg.from === "me" ? "#fff" : C.ink, padding: "10px 14px", fontSize: 14, fontFamily: FONT.body, lineHeight: 1.55, border: msg.from === "me" ? "none" : `1px solid ${C.border}` }}>
                    {msg.text}
                    <div style={{ fontSize: 10, opacity: 0.55, marginTop: 4, textAlign: "right" }}>{msg.time}</div>
                  </div>
                </div>
              ))}
              <div ref={bottomRef} />
            </div>
            {suggestions.length > 0 && (
              <div style={{ padding: "10px 14px", borderTop: `1px solid ${C.border}`, background: C.aiFaint }}>
                <div style={{ fontSize: 9, fontWeight: 700, color: C.ai, letterSpacing: "0.1em", marginBottom: 7, fontFamily: FONT.body }}>— AI SUGGESTIONS — TAP TO SEND</div>
                {suggestions.map((s, i) => <button key={i} onClick={() => send(s)} style={{ display: "block", width: "100%", background: C.white, border: `1px solid ${C.border}`, padding: "8px 12px", fontSize: 13, color: C.inkMid, fontFamily: FONT.body, cursor: "pointer", textAlign: "left", marginBottom: 5, lineHeight: 1.4 }}>{s}</button>)}
              </div>
            )}
            <div style={{ padding: "10px 12px", borderTop: `1px solid ${C.border}`, background: C.white, display: "flex", gap: 8 }}>
              <button onClick={getAI} style={{ padding: "9px 12px", border: `1px solid ${C.border}`, background: loadingSugg ? C.ai : C.aiFaint, color: loadingSugg ? "#fff" : C.ai, cursor: "pointer", fontWeight: 700, fontSize: 10, fontFamily: FONT.body, letterSpacing: "0.06em", display: "flex", alignItems: "center", gap: 5 }}>
                {loadingSugg ? <div style={{ width: 11, height: 11, border: "2px solid rgba(255,255,255,0.3)", borderTop: "2px solid #fff", borderRadius: "50%", animation: "spin 0.7s linear infinite" }} /> : "✦"} AI
              </button>
              <textarea value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(input); } }} placeholder="Write a message…" rows={1} style={{ flex: 1, border: `1px solid ${C.border}`, padding: "9px 12px", fontSize: 14, fontFamily: FONT.body, resize: "none", outline: "none", color: C.ink, background: C.surface, lineHeight: 1.5 }} />
              <button onClick={() => send(input)} style={{ padding: "9px 16px", background: C.orange, border: "none", color: "#fff", fontWeight: 700, fontSize: 14, cursor: "pointer" }}>↑</button>
            </div>
          </div>
        ) : (
          <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", background: C.surface }}>
            <div style={{ fontFamily: FONT.display, fontSize: 28, color: C.inkLight, letterSpacing: "0.06em" }}>SELECT A CONVERSATION</div>
          </div>
        )}
      </div>
    </div>
  );
};

// ── GROUPS ────────────────────────────────────────────────────────────────────
const GroupsView = ({ communities, setCommunities }) => (
  <div>
    <h2 style={{ fontFamily: FONT.display, fontSize: 48, color: C.ink, letterSpacing: "0.04em", marginBottom: 4, lineHeight: 1 }}>GROUPS</h2>
    <p style={{ color: C.inkLight, fontSize: 13, fontFamily: FONT.body, marginBottom: 24 }}>Professional communities for every educator</p>
    {communities.map((c, i) => (
      <div key={c.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "18px 0", borderBottom: `1px solid ${C.border}`, borderTop: i === 0 ? `1px solid ${C.border}` : "none" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <div style={{ width: 4, height: 44, background: c.joined ? C.orange : C.border, flexShrink: 0, transition: "background 0.2s" }} />
          <div>
            <div style={{ fontFamily: FONT.display, fontSize: 22, color: C.ink, letterSpacing: "0.04em", lineHeight: 1 }}>{c.name.toUpperCase()}</div>
            <div style={{ color: c.joined ? C.orange : C.inkFaint, fontSize: 10, fontWeight: 700, fontFamily: FONT.body, marginTop: 4, letterSpacing: "0.08em" }}>{c.members.toLocaleString()} MEMBERS</div>
          </div>
        </div>
        <button onClick={() => setCommunities(cs => cs.map(x => x.id === c.id ? { ...x, joined: !x.joined } : x))} style={{ padding: "9px 20px", fontSize: 10, fontWeight: 700, fontFamily: FONT.body, cursor: "pointer", letterSpacing: "0.08em", border: c.joined ? `1px solid ${C.border}` : `1px solid ${C.orange}`, background: c.joined ? "transparent" : C.orange, color: c.joined ? C.inkLight : "#fff", transition: "all 0.2s" }}>
          {c.joined ? "JOINED ✓" : "JOIN →"}
        </button>
      </div>
    ))}
  </div>
);

// ── PROFILE ───────────────────────────────────────────────────────────────────
const ProfileView = ({ connected }) => (
  <div>
    <div style={{ borderBottom: `1px solid ${C.border}`, paddingBottom: 28, marginBottom: 28 }}>
      <div style={{ display: "flex", gap: 20, alignItems: "flex-start", marginBottom: 20 }}>
        <Av i={ME.avatar} size={80} />
        <div>
          <div style={{ fontFamily: FONT.display, fontSize: 52, color: C.ink, letterSpacing: "0.03em", lineHeight: 0.92, marginBottom: 10 }}>{ME.name.toUpperCase()}</div>
          <div style={{ color: C.orange, fontWeight: 700, fontSize: 14, fontFamily: FONT.body }}>{ME.role}</div>
          <div style={{ color: C.inkLight, fontSize: 13, fontFamily: FONT.body, marginTop: 2 }}>{ME.school} · {ME.country}</div>
        </div>
      </div>
      <div style={{ borderLeft: `3px solid ${C.orange}`, paddingLeft: 14, marginBottom: 16 }}>
        <p style={{ color: C.inkMid, fontSize: 14, lineHeight: 1.7, fontFamily: FONT.body, margin: 0 }}>{ME.bio}</p>
      </div>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
        {ME.subjects.map(s => <Tag key={s}>{s}</Tag>)}
        <Tag accent>{ME.phase}</Tag>
        <Tag accent>{ME.yearsExp} yrs exp</Tag>
      </div>
    </div>
    <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 1, background: C.border, marginBottom: 28 }}>
      {[["CONNECTIONS", ME.connections + connected.length, true], ["POSTS", ME.posts, false], ["CPD BADGES", ME.cpd, false]].map(([label, val, accent]) => (
        <div key={label} style={{ background: C.white, padding: "20px 16px", textAlign: "center" }}>
          <div style={{ fontFamily: FONT.display, fontSize: 40, color: accent ? C.orange : C.ink, letterSpacing: "0.04em", lineHeight: 1 }}>{val}</div>
          <div style={{ fontSize: 9, color: C.inkFaint, fontWeight: 700, letterSpacing: "0.1em", fontFamily: FONT.body, marginTop: 6 }}>{label}</div>
        </div>
      ))}
    </div>
    <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 24 }}>
      <div style={{ fontFamily: FONT.display, fontSize: 24, color: C.ink, letterSpacing: "0.04em", marginBottom: 16 }}>CPD BADGES</div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        {[{ name: "Collaborator", earned: true }, { name: "Sharer", earned: true }, { name: "Mentor", earned: false }, { name: "Innovator", earned: false }, { name: "Leader", earned: false }].map(b => (
          <div key={b.name} style={{ padding: "14px 20px", border: `1px solid ${b.earned ? C.orange + "60" : C.border}`, background: b.earned ? C.orangeFaint : C.surface, opacity: b.earned ? 1 : 0.45 }}>
            <div style={{ fontFamily: FONT.display, fontSize: 20, color: b.earned ? C.orange : C.inkFaint, letterSpacing: "0.05em" }}>{b.earned ? "●" : "○"} {b.name.toUpperCase()}</div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

// ── MAIN ──────────────────────────────────────────────────────────────────────
export default function Edifyi() {
  const [tab, setTab] = useState("home");
  const [connected, setConnected] = useState([]);
  const [posts, setPosts] = useState(POSTS);
  const [communities, setCommunities] = useState(COMMUNITIES);
  const [conversations, setConversations] = useState([
    { id: 1, teacher: TEACHERS[0], messages: [{ from: "them", text: "Hi Alex — saw your work on decolonising the curriculum. I'd love to exchange ideas on how you approach it.", time: "10:24" }, { from: "me", text: "Priya! Yes — it's been a real journey. We've restructured our whole Year 9 scheme. What's your context?", time: "10:31" }], unread: 0 },
    { id: 2, teacher: TEACHERS[1], messages: [{ from: "them", text: "Fellow Head of Humanities here. Would love to run a comparative lesson — African and British perspectives on colonial history, simultaneously.", time: "Yesterday" }], unread: 1 },
  ]);
  const [toast, setToast] = useState(null);

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(null), 2500); };
  const totalUnread = conversations.reduce((n, c) => n + c.unread, 0);

  const NAV = [
    { id: "discover", label: "DISCOVER" },
    { id: "feed", label: "FEED" },
    { id: "messages", label: totalUnread > 0 ? `MESSAGES (${totalUnread})` : "MESSAGES" },
    { id: "groups", label: "GROUPS" },
    { id: "profile", label: "PROFILE" },
  ];

  return (
    <div style={{ minHeight: "100vh", background: C.white, fontFamily: FONT.body }}>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } } * { box-sizing: border-box; margin: 0; padding: 0; }`}</style>
      <link href="https://fonts.googleapis.com/css2?family=Bebas+Neue&family=DM+Sans:wght@400;500;600;700&display=swap" rel="stylesheet" />
      <Toast msg={toast} />

      {/* NAV */}
      <div style={{ background: C.black, position: "sticky", top: 0, zIndex: 100, display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 28px", height: 52 }}>
        <button onClick={() => setTab("home")} style={{ background: "none", border: "none", cursor: "pointer", display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{ width: 8, height: 8, background: C.orange, borderRadius: "50%" }} />
          <span style={{ fontFamily: FONT.display, fontSize: 22, color: C.white, letterSpacing: "0.14em" }}>EDIFYI</span>
        </button>
        <div style={{ display: "flex" }}>
          {NAV.map(n => (
            <NavBtn key={n.id} label={n.label} active={tab === n.id} onClick={() => setTab(n.id)} />
          ))}
        </div>
        <Av i={ME.avatar} size={30} />
      </div>

      {/* HOME */}
      {tab === "home" && (
        <div>
          <div style={{ padding: "80px 48px 72px", maxWidth: 900 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: C.inkFaint, letterSpacing: "0.18em", fontFamily: FONT.body, marginBottom: 20 }}>EDUCATION'S PROFESSIONAL NETWORK</div>
            <div style={{ fontFamily: FONT.display, fontSize: 100, lineHeight: 0.9, color: C.ink, letterSpacing: "0.02em", marginBottom: 32 }}>
              EDUCATION<br />IS{" "}<CyclingWord />
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 24 }}>
              <div style={{ fontFamily: FONT.display, fontSize: 72, color: C.orange, letterSpacing: "0.1em", lineHeight: 1 }}>EDIFYI</div>
              <div style={{ borderLeft: `3px solid ${C.border}`, paddingLeft: 16 }}>
                <div style={{ fontFamily: FONT.body, fontSize: 11, fontWeight: 700, color: C.inkFaint, letterSpacing: "0.1em", lineHeight: 2.2 }}>
                  <div><span style={{ color: C.orange, fontSize: 14 }}>E</span> — Empowering</div>
                  <div><span style={{ color: C.orange, fontSize: 14 }}>D</span> — Discovery</div>
                  <div><span style={{ color: C.orange, fontSize: 14 }}>I</span> — Inspiring</div>
                  <div><span style={{ color: C.orange, fontSize: 14 }}>F</span> — Flourishing</div>
                  <div><span style={{ color: C.orange, fontSize: 14 }}>Y</span> — Yours</div>
                  <div><span style={{ color: C.orange, fontSize: 14 }}>I</span> — Inclusive</div>
                </div>
              </div>
            </div>
            <p style={{ color: C.inkLight, fontSize: 16, lineHeight: 1.75, fontFamily: FONT.body, maxWidth: 500, marginBottom: 12 }}>
              Edifyi connects teachers and leaders in a professional space built for sharing, growth and genuine collaboration — globally.
            </p>
            <p style={{ color: C.orange, fontSize: 13, fontWeight: 700, fontFamily: FONT.body, letterSpacing: "0.06em", marginBottom: 36 }}>
              THE PROFESSIONAL NETWORK WHERE EVERY EDUCATOR IS SEEN.
            </p>
            <div style={{ display: "flex", gap: 12 }}>
              <button onClick={() => setTab("discover")} style={{ background: C.orange, color: "#fff", border: "none", padding: "15px 36px", fontSize: 12, fontWeight: 700, fontFamily: FONT.body, cursor: "pointer", letterSpacing: "0.09em" }}>DISCOVER EDUCATORS →</button>
              <button onClick={() => setTab("feed")} style={{ background: "transparent", color: C.ink, border: `1px solid ${C.border}`, padding: "15px 36px", fontSize: 12, fontWeight: 700, fontFamily: FONT.body, cursor: "pointer", letterSpacing: "0.09em" }}>VIEW FEED</button>
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", borderTop: `1px solid ${C.border}`, borderBottom: `1px solid ${C.border}` }}>
            {[
              { num: "01", title: "CONNECT", body: "Smart AI matching finds educators who genuinely complement your practice — not just your subject area." },
              { num: "02", title: "SHARE", body: "A professional feed where ideas, resources and leadership insights travel across borders." },
              { num: "03", title: "EDIFYI", body: "Build your professional profile. Log CPD. Promote yourself. Grow your career." },
            ].map((p, i) => (
              <div key={p.num} style={{ padding: "44px 36px", borderRight: i < 2 ? `1px solid ${C.border}` : "none", borderLeft: i === 0 ? `4px solid ${C.orange}` : "none" }}>
                <div style={{ fontFamily: FONT.display, fontSize: 11, color: C.orange, letterSpacing: "0.18em", marginBottom: 14 }}>{p.num}</div>
                <div style={{ fontFamily: FONT.display, fontSize: 36, color: C.ink, letterSpacing: "0.04em", marginBottom: 14, lineHeight: 1 }}>{p.title}</div>
                <p style={{ color: C.inkLight, fontSize: 14, lineHeight: 1.7, fontFamily: FONT.body }}>{p.body}</p>
              </div>
            ))}
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", background: C.black }}>
            {[["12,400+", "EDUCATORS"], ["84", "COUNTRIES"], ["18,400", "LEADERS"], ["96%", "MATCH RATE"]].map(([num, label], i) => (
              <div key={label} style={{ padding: "36px 24px", textAlign: "center", borderRight: i < 3 ? "1px solid #1f1f1f" : "none" }}>
                <div style={{ fontFamily: FONT.display, fontSize: 44, color: C.orange, letterSpacing: "0.04em", lineHeight: 1 }}>{num}</div>
                <div style={{ fontSize: 9, color: "#555", fontWeight: 700, letterSpacing: "0.14em", fontFamily: FONT.body, marginTop: 8 }}>{label}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* INNER VIEWS */}
      {tab !== "home" && (
        <div style={{ maxWidth: 760, margin: "0 auto", padding: "36px 28px 60px" }}>
          {tab === "discover" && <DiscoverView connected={connected} setConnected={setConnected} conversations={conversations} setConversations={setConversations} showToast={showToast} />}
          {tab === "feed" && <FeedView posts={posts} setPosts={setPosts} showToast={showToast} />}
          {tab === "messages" && <MessagesView conversations={conversations} setConversations={setConversations} />}
          {tab === "groups" && <GroupsView communities={communities} setCommunities={setCommunities} />}
          {tab === "profile" && <ProfileView connected={connected} />}
        </div>
      )}
    </div>
  );
}
